import { Link } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import type { AchievementItem } from '@/mocks/achievements';
import {
  RailwayTiesPattern,
  RailwayCrosshatch,
  RailwayTrackMarks,
  RailwayTrackBorder,
  RailwayWatermark,
} from '@/components/feature/RailwayPatterns';

interface ModuleOverviewProps {
  achievement: AchievementItem;
}

interface SubItem {
  id: string;
  sectionId: string;
  tabKey: string;
  title: string;
}

interface TabModule {
  tabKey: string;
  tabLabel: string;
  tabLabelEn: string;
  icon: string;
  items: SubItem[];
}

function createModules(): TabModule[] {
  return [
    {
      tabKey: 'application',
      tabLabel: '申报书',
      tabLabelEn: 'Application',
      icon: 'ri-article-line',
      items: [
        { id: 'app-form', sectionId: 'app-form', tabKey: 'application', title: '申报书预览' },
        { id: 'app-awards', sectionId: 'app-awards', tabKey: 'application', title: '成果曾获奖励' },
        { id: 'app-timeline', sectionId: 'app-timeline', tabKey: 'application', title: '成果起止时间' },
        { id: 'section-contributors', sectionId: 'section-contributors', tabKey: 'application', title: '主要完成人情况' },
        { id: 'section-units', sectionId: 'section-units', tabKey: 'application', title: '主要完成单位情况' },
      ],
    },
    {
      tabKey: 'certification',
      tabLabel: '鉴定证书',
      tabLabelEn: 'Certificate',
      icon: 'ri-shield-check-line',
      items: [
        { id: 'cert-assoc', sectionId: 'cert-assoc', tabKey: 'certification', title: '协会鉴定' },
        { id: 'cert-edu', sectionId: 'cert-edu', tabKey: 'certification', title: '教育厅鉴定' },
      ],
    },
    {
      tabKey: 'report',
      tabLabel: '成果报告',
      tabLabelEn: 'Report',
      icon: 'ri-bar-chart-box-line',
      items: [
        { id: 'rep-background', sectionId: 'rep-background', tabKey: 'report', title: '成果研究背景' },
        { id: 'rep-approach', sectionId: 'rep-approach', tabKey: 'report', title: '成果主要做法' },
        { id: 'rep-innovation', sectionId: 'rep-innovation', tabKey: 'report', title: '成果主要创新' },
        { id: 'rep-promotion', sectionId: 'rep-promotion', tabKey: 'report', title: '成果应用推广' },
      ],
    },
    {
      tabKey: 'materials',
      tabLabel: '支撑材料',
      tabLabelEn: 'Materials',
      icon: 'ri-folder-2-line',
      items: [
        { id: 'mat-honors', sectionId: 'mat-honors', tabKey: 'materials', title: '专业所获荣誉' },
        { id: 'mat-curriculum', sectionId: 'mat-curriculum', tabKey: 'materials', title: '课程建设' },
        { id: 'mat-textbooks', sectionId: 'mat-textbooks', tabKey: 'materials', title: '教材建设' },
        { id: 'mat-platform', sectionId: 'mat-platform', tabKey: 'materials', title: '平台建设' },
        { id: 'mat-faculty', sectionId: 'mat-faculty', tabKey: 'materials', title: '师资队伍建设' },
        { id: 'mat-party', sectionId: 'mat-party', tabKey: 'materials', title: '党建思政建设' },
        { id: 'mat-reform-projects', sectionId: 'mat-reform-projects', tabKey: 'materials', title: '代表性教改研究项目' },
        { id: 'mat-industry-cooperation', sectionId: 'mat-industry-cooperation', tabKey: 'materials', title: '产教合作基地建设' },
        { id: 'mat-international', sectionId: 'mat-international', tabKey: 'materials', title: '国际化及卓越人才培养' },
        { id: 'mat-student-awards', sectionId: 'mat-student-awards', tabKey: 'materials', title: '学生学科竞赛获奖与创新创业项目' },
        { id: 'mat-outstanding-graduates', sectionId: 'mat-outstanding-graduates', tabKey: 'materials', title: '优秀毕业生典型' },
        { id: 'mat-enterprise-evaluation', sectionId: 'mat-enterprise-evaluation', tabKey: 'materials', title: '企业评价' },
        { id: 'mat-social-impact', sectionId: 'mat-social-impact', tabKey: 'materials', title: '社会影响' },
        { id: 'mat-application', sectionId: 'mat-application', tabKey: 'materials', title: '成果应用' },
      ],
    },
  ];
}

function ModuleBlock({
  module,
  achievementId,
  visible,
  index,
}: {
  module: TabModule;
  achievementId: string;
  visible: boolean;
  index: number;
}) {
  const [showMore, setShowMore] = useState(false);
  const displayItems = showMore ? module.items : module.items.slice(0, 4);
  const hasMore = module.items.length > 4;

  const watermarkIcons: Record<string, string> = {
    'ri-article-line': 'ri-file-list-3-line',
    'ri-shield-check-line': 'ri-medal-line',
    'ri-folder-2-line': 'ri-award-line',
    'ri-bar-chart-box-line': 'ri-bar-chart-line',
  };

  return (
    <div
      className="relative overflow-hidden rounded-lg bg-white"
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0) scale(1)' : 'translateY(60px) scale(0.88)',
        filter: visible ? 'blur(0)' : 'blur(3px)',
        transition: `opacity 0.7s cubic-bezier(0.16,1,0.22,1) ${0.05 + index * 0.15}s, transform 0.7s cubic-bezier(0.16,1,0.22,1) ${0.05 + index * 0.15}s, filter 0.7s cubic-bezier(0.16,1,0.22,1) ${0.05 + index * 0.15}s`,
        boxShadow: visible ? '0 2px 16px rgba(0,0,0,0.07)' : 'none',
      }}
    >
      {/* Header bar */}
      <div className="relative h-[56px] md:h-[64px] flex items-center bg-[#B22222] text-white overflow-hidden">
        <RailwayTrackMarks />
        <div className="relative z-10 flex items-center gap-2.5 px-5 md:px-6">
          <div className="w-7 h-7 flex items-center justify-center">
            <i className={`${module.icon} text-lg md:text-xl`} />
          </div>
          <div>
            <span className="text-base md:text-lg font-semibold tracking-wide">{module.tabLabel}</span>
            <span className="block text-[10px] text-white/55 font-medium tracking-[0.1em] uppercase mt-0.5">{module.tabLabelEn}</span>
          </div>
        </div>
        {hasMore && (
          <button
            onClick={() => setShowMore(!showMore)}
            className="absolute right-5 z-10 text-sm text-white/80 hover:text-white transition-colors cursor-pointer"
          >
            {showMore ? '收起' : '更多 +'}
          </button>
        )}
        <div className="absolute -right-10 -top-10 w-[120px] h-[120px] bg-[#8B0000] rotate-45" style={{ opacity: 0.4 }} />
      </div>

      {/* Items */}
      <div className="relative p-5 md:p-6 grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
        <RailwayTiesPattern />
        <RailwayTrackBorder />
        <RailwayCrosshatch />
        <RailwayWatermark icon={watermarkIcons[module.icon] || 'ri-train-line'} />
        {displayItems.map((item) => {
          const linkTo = item.sectionId
            ? `/achievement/${achievementId}?tab=${item.tabKey}&section=${item.sectionId}`
            : `/achievement/${achievementId}?tab=${item.tabKey}`;
          return (
            <Link
              key={item.id}
              to={linkTo}
              className="relative z-10 group flex items-center gap-3 px-5 py-4 rounded-md bg-background-50 hover:bg-background-100 transition-all duration-300"
            >
              <span className="block w-[3px] h-5 rounded-full bg-[#B22222] shrink-0 transition-all duration-300 group-hover:h-6" />
              <span className="text-[14px] md:text-[15px] text-foreground-700 group-hover:text-[#B22222] transition-colors duration-300 line-clamp-1">
                {item.title}
              </span>
              <i className="ri-arrow-right-s-line text-sm text-foreground-300 ml-auto shrink-0 opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all duration-300" />
            </Link>
          );
        })}
      </div>
    </div>
  );
}

export default function ModuleOverview({ achievement }: ModuleOverviewProps) {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const modules = createModules();

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !visible) {
          setVisible(true);
          observer.unobserve(el);
        }
      },
      { threshold: 0.05 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [visible]);

  return (
    <section className="relative pt-4 md:pt-6 pb-12 md:pb-16 px-4 md:px-8 bg-background-50" ref={sectionRef}>
      <div className="max-w-[1200px] mx-auto">
        {/* Grid layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-5">
          {modules.map((mod, idx) => (
            <ModuleBlock
              key={mod.tabKey}
              module={mod}
              achievementId={achievement.id}
              visible={visible}
              index={idx}
            />
          ))}
        </div>
      </div>
    </section>
  );
}